import { CraAttributeName } from "@keita/lanepatrol-sdk";
import {
  CraNodeCoding,
  CreateOrUpdateCraNodeCoding,
} from "../../coding/models/CraNodeCoding";
import { CraNode } from "../../cra/models/CraNode";
import { CraWay } from "../../cra/models/CraWay";

export enum Col {
  coderName,
  codingDate,
  surveyName,
  surveyCity,
  routeName,
  photoDate,
  photoReference,
  photoName,
  distanceFromStart,
  distancetoNext,
  latitudeStart,
  longitudeStart,
  latitudeEnd,
  longitudeEnd,
  scoreGlobal,
  scoreVB,
  scoreBB,
  scoreBP,
  scoreSB,
  comment,
  areaType,
  facilityType,
  facilityAccess,
  looseOrSlipperySurface,
  tramOrTrainRails,
  majorSurfaceDeformationOrDrainOpening,
  fixedObstacleOnFacility,
  nonFixedObstacleOnFacility,
  lineOfSight,
  delineation,
  lightSegregation,
  facilityWidthPerDirection,
  flowDirection,
  widthRestriction,
  roadLane01,
  vehicleParking01,
  severeHazard01,
  objectOrLevelChange01,
  sidewalk01,
  roadLane13,
  vehicleParking13,
  severeHazard13,
  objectOrLevelChange13,
  sidewalk13,
  grade,
  curvature,
  streetLighting,
  pedestrianCrossing,
  intersectionBicycleFacility,
  intersectionApproach,
  intersectionOrRoadCrossing,
  crossingFacility,
  numberOfLanesAdjacentRoad,
  numberOfLanesIntersectingRoad,
  propertyAccess,
  peakPedestrianFlowAlongOrAcrossFacility,
  peakBicycleAndLVTrafficFlow,
  observedProportionOfCargoBikesAndMopeds,
  bicycleAndLVSpeedAverage,
  bicycleAndLVSpeedDifferential,
  roadAADT,
  heavyVehicleFlow,
  roadSpeedLimit,
  roadOperatingSpeed,
}

export const expectedCsvHeader = [
  "Coder name",
  "Coding date",
  "Survey name",
  "Survey city",
  "Cycle lane name",
  "Image capture date",
  "Image reference",
  "Image name",
  "Distance",
  "Length",
  "Latitude start",
  "Longitude start",
  "Latitude end",
  "Longitude end",
  "Section score global",
  "Section score VB",
  "Section score BB",
  "Section score BP",
  "Section score SB ",
  "Comments",
  "Area type",
  "Facility type",
  "Facility access",
  "Loose or slippery surface",
  "Tram or train rails",
  "Major surface deformation or drain opening",
  "Fixed obstacle on facility",
  "Non fixed obstacle on facility",
  "Line of sight",
  "Delineation",
  "Light segregation",
  "Facility width per direction",
  "Flow direction",
  "Width restriction",
  "Adjacent road lane - 0-1m",
  "Adjacent vehicle parking - 0-1m",
  "Adjacent severe hazard - 0-1m",
  "Adjacent object or level change - 0-1m",
  "Adjacent sidewalk - 0-1m",
  "Adjacent road lane - 1-3m",
  "Adjacent vehicle parking - 1-3m",
  "Adjacent severe hazard - 1-3m",
  "Adjacent object or level change - 1-3m",
  "Adjacent sidewalk - 1-3m",
  "Grade",
  "Curvature",
  "Street lighting",
  "Pedestrian crossing",
  "Intersecting bicycle facility",
  "Intersection approach",
  "Intersection or road crossing",
  "Crossing facility",
  "Number of lanes adjacent road",
  "Number of lanes intersecting road",
  "Property access",
  "Peak pedestrian flow along or across facility",
  "Peak bicycle and LV traffic flow",
  "Observed proportion of cargo bikes and mopeds",
  "Bicycle and LV speed average",
  "Bicycle and LV speed differential",
  "Road AADT",
  "Heavy vehicle flow",
  "Road speed limit",
  "Road operating speed",
];

export type CsvRowArray = string[];

export type OptionFieldConfig = {
  col: Col;
  attributeName: CraAttributeName;
  label: string;
};

export const attributeFields: OptionFieldConfig[] = [
  {
    col: Col.areaType,
    attributeName: CraAttributeName.AreaType,
    label: "Area type",
  },
  {
    col: Col.facilityType,
    attributeName: CraAttributeName.FacilityType,
    label: "Facility type",
  },
  {
    col: Col.facilityAccess,
    attributeName: CraAttributeName.FacilityAccess,
    label: "Facility access",
  },
  {
    col: Col.looseOrSlipperySurface,
    attributeName: CraAttributeName.LooseOrSlipperySurface,
    label: "Loose or slippery surface",
  },
  {
    col: Col.tramOrTrainRails,
    attributeName: CraAttributeName.TramOrTrainRails,
    label: "Tram or train rails",
  },
  {
    col: Col.majorSurfaceDeformationOrDrainOpening,
    attributeName: CraAttributeName.MajorSurfaceDeformationOrDrainOpening,
    label: "Major surface deformation or drain opening",
  },
  {
    col: Col.fixedObstacleOnFacility,
    attributeName: CraAttributeName.FixedObstacleOnFacility,
    label: "Fixed obstacle on facility",
  },
  {
    col: Col.nonFixedObstacleOnFacility,
    attributeName: CraAttributeName.NonFixedObstacleOnFacility,
    label: "Non fixed obstacle on facility",
  },
  {
    col: Col.lineOfSight,
    attributeName: CraAttributeName.LineOfSight,
    label: "Line of sight",
  },
  {
    col: Col.delineation,
    attributeName: CraAttributeName.Delineation,
    label: "Delineation",
  },
  {
    col: Col.lightSegregation,
    attributeName: CraAttributeName.LightSegregation,
    label: "Light segregation",
  },
  {
    col: Col.facilityWidthPerDirection,
    attributeName: CraAttributeName.FacilityWidthPerDirection,
    label: "Facility width per direction",
  },
  {
    col: Col.flowDirection,
    attributeName: CraAttributeName.FlowDirection,
    label: "Flow direction",
  },
  {
    col: Col.widthRestriction,
    attributeName: CraAttributeName.WidthRestriction,
    label: "Width restriction",
  },

  {
    col: Col.roadLane01,
    attributeName: CraAttributeName.AdjacentFeatures0to1mRoadLane,
    label: "Road lane - 0-1m",
  },
  {
    col: Col.vehicleParking01,
    attributeName: CraAttributeName.AdjacentFeatures0to1mVehicleParking,
    label: "Vehicle parking - 0-1m",
  },
  {
    col: Col.severeHazard01,
    attributeName: CraAttributeName.AdjacentFeatures0to1mSevereHazard,
    label: "Severe hazard - 0-1m",
  },
  {
    col: Col.objectOrLevelChange01,
    attributeName: CraAttributeName.AdjacentFeatures0to1mObjectOrLevelChange,
    label: "Object or level change - 0-1m",
  },
  {
    col: Col.sidewalk01,
    attributeName: CraAttributeName.AdjacentFeatures0to1mSidewalk,
    label: "Sidewalk - 0-1m",
  },

  {
    col: Col.roadLane13,
    attributeName: CraAttributeName.AdjacentFeatures1to3mRoadLane,
    label: "Road lane - 1-3m",
  },
  {
    col: Col.vehicleParking13,
    attributeName: CraAttributeName.AdjacentFeatures1to3mVehicleParking,
    label: "Vehicle parking - 1-3m",
  },
  {
    col: Col.severeHazard13,
    attributeName: CraAttributeName.AdjacentFeatures1to3mSevereHazard,
    label: "Severe hazard - 1-3m",
  },
  {
    col: Col.objectOrLevelChange13,
    attributeName: CraAttributeName.AdjacentFeatures1to3mObjectOrLevelChange,
    label: "Object or level change - 1-3m",
  },
  {
    col: Col.sidewalk13,
    attributeName: CraAttributeName.AdjacentFeatures1to3mSidewalk,
    label: "Sidewalk - 1-3m",
  },

  { col: Col.grade, attributeName: CraAttributeName.Grade, label: "Grade" },
  {
    col: Col.curvature,
    attributeName: CraAttributeName.Curvature,
    label: "Curvature",
  },
  {
    col: Col.streetLighting,
    attributeName: CraAttributeName.StreetLighting,
    label: "Street lighting",
  },
  {
    col: Col.pedestrianCrossing,
    attributeName: CraAttributeName.PedestrianCrossing,
    label: "Pedestrian crossing",
  },
  {
    col: Col.intersectionBicycleFacility,
    attributeName: CraAttributeName.IntersectionBicycleFacility,
    label: "Intersection bicycle facility",
  },
  {
    col: Col.intersectionApproach,
    attributeName: CraAttributeName.IntersectionApproach,
    label: "Intersection approach",
  },
  {
    col: Col.intersectionOrRoadCrossing,
    attributeName: CraAttributeName.IntersectionOrRoadCrossing,
    label: "Intersection or road crossing",
  },
  {
    col: Col.crossingFacility,
    attributeName: CraAttributeName.CrossingFacility,
    label: "Crossing facility",
  },
  {
    col: Col.numberOfLanesAdjacentRoad,
    attributeName: CraAttributeName.NumberOfLanesAdjacentRoad,
    label: "Number of lanes adjacent road",
  },
  {
    col: Col.numberOfLanesIntersectingRoad,
    attributeName: CraAttributeName.NumberOfLanesIntersectingRoad,
    label: "Number of lanes intersecting road",
  },
  {
    col: Col.propertyAccess,
    attributeName: CraAttributeName.PropertyAccess,
    label: "Property access",
  },
  {
    col: Col.peakPedestrianFlowAlongOrAcrossFacility,
    attributeName: CraAttributeName.PeakPedestrianFlowAlongOrAcrossFacility,
    label: "Peak pedestrian flow along or across facility",
  },
  {
    col: Col.peakBicycleAndLVTrafficFlow,
    attributeName: CraAttributeName.PeakBicycleAndLVTrafficFlow,
    label: "Peak bicycle and LV traffic flow",
  },
  {
    col: Col.observedProportionOfCargoBikesAndMopeds,
    attributeName: CraAttributeName.ObservedProportionOfCargoBikesAndMopeds,
    label: "Observed proportion of cargo bikes and mopeds",
  },
  {
    col: Col.bicycleAndLVSpeedAverage,
    attributeName: CraAttributeName.BicycleAndLVSpeedAverage,
    label: "Bicycle and LV speed average",
  },
  {
    col: Col.bicycleAndLVSpeedDifferential,
    attributeName: CraAttributeName.BicycleAndLVSpeedDifferential,
    label: "Bicycle and LV speed differential",
  },
  {
    col: Col.roadAADT,
    attributeName: CraAttributeName.RoadAADT,
    label: "Road AADT",
  },
  {
    col: Col.heavyVehicleFlow,
    attributeName: CraAttributeName.HeavyVehicleFlow,
    label: "Heavy vehicle flow",
  },
];

export interface ImportCodingCsvParams {
  file: File;
  selectedWay: CraWay;
  nodes: CraNode[];
  codingsByNodeId: Record<string, CraNodeCoding>;
}

export interface ImportCodingCsvResult {
  entries: CreateOrUpdateCraNodeCoding[];
  errors: string[];
}
